export const dentalSectionPhaseEvent = "dental-section-phase";

const revealDurationMs = 800;

export function installDentalSectionReveal(main: HTMLElement) {
  const document = main.ownerDocument;
  const browser = document.defaultView;
  if (!browser) return () => {};
  const reducedMotion = browser.matchMedia("(prefers-reduced-motion: reduce)");
  const sections = Array.from(main.querySelectorAll<HTMLElement>(":scope > section"));
  const phase = (section: HTMLElement, ready: boolean) => {
    if (section.dataset.revealReady === String(ready)) return;
    section.dataset.revealReady = String(ready);
    section.dispatchEvent(new Event(dentalSectionPhaseEvent));
  };
  const targets = sections.flatMap((section, index) => {
    const content = section.querySelector<HTMLElement>(":scope > :not([aria-hidden]):not(.pointer-events-none)");
    if (!content) return [];
    content.dataset.dentalRevealContent = "";
    return [{
      section, content, offset: 0, startedAt: 0,
      // Keep the initial hero readable; it also fades when revisited later.
      state: index === 0 ? "shown" : "hidden",
      originalOpacity: content.style.opacity, originalTranslate: content.style.translate,
    }];
  });
  let frame = 0;
  let disposed = false;

  const update = (now: number) => {
    frame = 0;
    if (disposed) return;
    const height = browser.innerHeight;
    let animating = false;
    // Use actual content bounds and remove our visual offset from the measurement.
    const measurements = targets.map((target) => {
      const rect = target.content.getBoundingClientRect();
      return { target, top: rect.top - target.offset, bottom: rect.bottom - target.offset };
    });
    for (const { target, top, bottom } of measurements) {
      const { section, content } = target;
      const focused = document.activeElement !== document.body && content.contains(document.activeElement);
      const staticContent = reducedMotion.matches || focused;
      const outside = bottom <= height * 0.08 || top >= height * 0.92;
      const entering = top <= height * 0.8 && bottom >= height * 0.2;
      if (staticContent) {
        target.state = "shown";
      } else if (outside) {
        target.state = "hidden";
      } else if (entering && target.state === "hidden") {
        target.state = "entering";
        target.startedAt = now;
      }

      let opacity = target.state === "shown" ? 1 : 0;
      if (target.state === "entering") {
        // Time-based fading remains visible even when a wheel flick, anchor link,
        // or centering assist moves an entire section into view in a single frame.
        const progress = Math.min(1, Math.max(0, (now - target.startedAt) / revealDurationMs));
        opacity = progress * progress * (3 - 2 * progress);
        if (progress === 1) target.state = "shown";
        else animating = true;
      }
      target.offset = (1 - opacity) * 24;
      content.style.opacity = String(opacity);
      content.style.translate = `0 ${target.offset}px`;
      phase(section, target.state === "shown");
    }
    if (animating) schedule();
  };
  const schedule = () => {
    if (!frame && !disposed) frame = browser.requestAnimationFrame(update);
  };
  sections.forEach((section, index) => phase(section, index === 0));
  update(browser.performance.now());
  // Input remains native; this controller only animates the content's appearance.
  browser.addEventListener("scroll", schedule, { passive: true });
  browser.addEventListener("resize", schedule);
  main.addEventListener("focusin", schedule);
  main.addEventListener("focusout", schedule);
  reducedMotion.addEventListener("change", schedule);
  const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(schedule);
  resizeObserver?.observe(main);
  targets.forEach(({ content }) => resizeObserver?.observe(content));

  return () => {
    disposed = true;
    browser.cancelAnimationFrame(frame);
    browser.removeEventListener("scroll", schedule);
    browser.removeEventListener("resize", schedule);
    main.removeEventListener("focusin", schedule);
    main.removeEventListener("focusout", schedule);
    reducedMotion.removeEventListener("change", schedule);
    resizeObserver?.disconnect();
    targets.forEach(({ content, originalOpacity, originalTranslate }) => {
      content.style.opacity = originalOpacity;
      content.style.translate = originalTranslate;
      delete content.dataset.dentalRevealContent;
    });
    sections.forEach((section) => delete section.dataset.revealReady);
  };
}
